// dependencies
import { arrayMove } from "@dnd-kit/sortable";
import React, { useMemo } from "react";

import { faCircleXmark, faGripDotsVertical } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import DragAndDrop from "../../../../../../components/DragAndDrop";
// components
import InfoLabel from "../../../../../../components/InfoLabel";
import SelectInput from "../../../../../../components/Inputs/SelectInput";
import Toggle from "../../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";

// store
import useReportStore from "../../store";

// config
import Config from "../../Config";
// styles
import css from "./SummarizeRows.module.scss";

const toggleTooltipText = {
	reviewReport:
		"Display all columns, including those that are not aggregatable. Those columns will only display a review form count.",
	employeeReport:
		"Display all columns, including those that are not aggregatable. Those columns will only display an employee count.",
	goalReport:
		"Display all columns, including those that are not aggregatable. Those columns will only display a goal count.",
	surveyReport:
		"Display all columns, including those that are not aggregatable. Those columns will only display a survey count.",
};

interface BaseProps {
	updatePagination: (pagination: { pageSize: number; pageNumber: number }) => void;
	handleSort: (sort: any) => void;
}

const SummarizeRows = (props: BaseProps) => {
	const { updatePagination, handleSort } = props;

	const [
		report,
		isSummarizeRowsView,
		setIsSummarizeRowsView,
		availableSummarizeFields,
		enabledSummarizeFields,
		setEnabledSummarizeFields,
		setShowAllAggregationColumns,
		getGridSorts,
		calculateShowAllColumnsToggleValue,
	] = useReportStore((state) => [
		state.report,
		state.isSummarizeRowsView,
		state.setIsSummarizeRowsView,
		state.availableSummarizeFields,
		state.enabledSummarizeFields,
		state.setEnabledSummarizeFields,
		state.setShowAllAggregationColumns,
		state.getGridSorts,
		state.calculateShowAllColumnsToggleValue,
	]);
	// formats options for summmarize fields select dropdown
	const summarizeFieldsOptions = useMemo(() => {
		// 1. iterate through availableSummarizeFields
		// 2. check if field is enabled, if it is, proceed to add to options
		// 3. check which group field belongs to, if it doesn't exist, create group,
		//	if it does, add field to group options
		return availableSummarizeFields.reduce((acc, field) => {
			const isEnabled = enabledSummarizeFields.find((enabledField) => enabledField.value === field.value);

			if (!isEnabled) {
				const fieldGroup = acc.find((group) => group.value === field.group);

				if (!fieldGroup) {
					acc.push({
						value: field.group,
						label: Config.groupLabelLookup[field.group],
						options: [{ label: field.label, value: field.value }],
					});
				} else {
					fieldGroup.options.push({ label: field.label, value: field.value });
				}
			}
			return acc;
		}, []);
	}, [enabledSummarizeFields.length]);

	const handleToggleSummarizeRows = (val) => {
		setIsSummarizeRowsView(val);
		// For now, clear sort when toggling to detailed view
		if (val === false) {
			handleSort([]);
		}
		// For now, set default sort order when toggling to summarized view
		if (val === true) {
			const formattedSort = formatSort(enabledSummarizeFields);
			handleSort(formattedSort);
		}
		updatePagination({ pageSize: 50, pageNumber: 1 });
	};

	const handleToggleShowAllReportColumns = (val) => {
		setShowAllAggregationColumns(val);
	};

	const formatSort = (sortItems) => {
		const gridSorts = getGridSorts();
		return sortItems.map((item, index) => {
			const foundSort = gridSorts.find((sort) => sort.colId === `summary_${item.value}`);
			return { colId: `summary_${item.value}`, sort: foundSort ? foundSort.sort : "asc", sortIndex: index };
		});
	};

	const handleAddSummarizeField = (val) => {
		if (val === null) return;
		// if adding first field, automatically toggle on summarize rows view
		if (enabledSummarizeFields.length === 0) {
			setIsSummarizeRowsView(true);
		}
		const summarizeFieldInfo = availableSummarizeFields.find((item) => item.value === val.value);
		let selection: null | string = null;

		// if the summarizeField is a date type, add the default secondary selection
		if (summarizeFieldInfo.type === "date") {
			selection = "month";
		}

		if (Config.binnableTypes.includes(summarizeFieldInfo.type)) {
			selection = "automatic";
		}

		setEnabledSummarizeFields([...enabledSummarizeFields, { ...val, selection }]);

		const formattedSort = formatSort([...enabledSummarizeFields, val]);
		handleSort(formattedSort);
	};

	const handleDragEnd = (event) => {
		const { active, over } = event;

		if (active.id !== over.id) {
			const activeIndex = enabledSummarizeFields.findIndex((item) => item.value === active.id);
			const overIndex = enabledSummarizeFields.findIndex((item) => item.value === over.id);
			const orderedChecklist = arrayMove(enabledSummarizeFields, activeIndex, overIndex);

			setEnabledSummarizeFields(orderedChecklist);
			handleSort(formatSort(orderedChecklist));
		}
	};

	const formatDraggableItems = (field) => {
		return (
			<div className={css.draggableSummarizeField}>
				<div className={css.summarizeFieldLeft}>
					<FontAwesomeIcon className={css.gripIcon} icon={faGripDotsVertical} />
					<div>{field.label}</div>
				</div>
				<div className={css.removeSummarizeField} onClick={() => handleRemoveSummarizeField(field)}>
					<FontAwesomeIcon icon={faCircleXmark} />
				</div>
			</div>
		);
	};

	const handleRemoveSummarizeField = (field) => {
		const filteredEnabledFields = enabledSummarizeFields.filter((item) => item.value !== field.value);
		setEnabledSummarizeFields(filteredEnabledFields);

		// if no more summarized row fields, turn off summarize view
		if (filteredEnabledFields.length === 0) {
			setIsSummarizeRowsView(false);
		}
		const formattedSort = formatSort(filteredEnabledFields);
		handleSort(formattedSort);
		updatePagination({ pageSize: 50, pageNumber: 1 });
	};

	return (
		<div className={css.summarizeRowsContainer}>
			<div className={css.panelHeader}>
				<div className={css.headerTitle}>Summarize Rows</div>
				{enabledSummarizeFields.length > 0 ? (
					<Toggle value={isSummarizeRowsView} onChange={handleToggleSummarizeRows} />
				) : null}
			</div>
			<SelectInput
				results={summarizeFieldsOptions}
				onChange={handleAddSummarizeField}
				placeholder="Search field..."
				ariaLabel="Search fields"
				value=""
				setFirstResult={false}
				isClearable={true}
				disabled={enabledSummarizeFields.length > 3}
			/>
			<div className={css.summarizeFieldsPool}>
				{enabledSummarizeFields.length === 0 ? (
					<div className={css.summarizeFieldsDescription}>Select up to 4 fields to summarize</div>
				) : null}
				<DragAndDrop
					itemIds={enabledSummarizeFields.map((item) => item.value)}
					draggableItems={enabledSummarizeFields}
					handleOnDragEnd={handleDragEnd}
					formatDraggableItems={formatDraggableItems}
					keyValue="value"
					itemsContainerClassName={css.droppableArea}
				/>
			</div>
			{isSummarizeRowsView ? (
				<div className={css.optionsContainer}>
					<div className={css.optionsLabel}>Options</div>
					<div className={css.showAllReportColumns}>
						<Toggle
							value={calculateShowAllColumnsToggleValue()}
							onChange={handleToggleShowAllReportColumns}
						/>
						<InfoLabel
							label="Show All Report Columns"
							title="Toggle all report columns"
							content={report?.type ? toggleTooltipText[report?.type] : ""}
						/>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default SummarizeRows;
